import React, { createContext, useContext, useMemo } from 'react';
import { getMessagesForLocale } from '@/lib/messages';

type Messages = Record<string, unknown>;

type I18nContextValue = {
  locale: string;
  messages: Messages;
};

const I18nContext = createContext<I18nContextValue>({
  locale: 'en-US',
  messages: getMessagesForLocale('en-US') as Messages,
});

const getNestedValue = (messages: Messages, path: string) =>
  path.split('.').reduce<unknown>((value, key) => {
    if (value && typeof value === 'object' && key in (value as Messages)) {
      return (value as Messages)[key];
    }

    return undefined;
  }, messages);

export const NextIntlClientProvider = ({
  locale,
  messages,
  children,
}: {
  locale: string;
  messages: Messages;
  children: React.ReactNode;
}) => {
  const value = useMemo(() => ({ locale, messages }), [locale, messages]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useTranslations = (namespace?: string) => {
  const { messages } = useContext(I18nContext);

  return (key: string) => {
    const path = namespace ? `${namespace}.${key}` : key;
    const value = getNestedValue(messages, path);

    return typeof value === 'string' ? value : path;
  };
};
